import React, { useEffect, useState } from 'react';
import {
  makeStyles,
  Box,
  TextField,
  Button,
  useTheme,
  useMediaQuery,
  Typography,
} from '@material-ui/core';

import useHandleResponse from 'utils/useHandleResponse';
import axios, { API } from 'config/axios';
import {
  useLayoutSnackbar,
  useLayoutLoadingSpinner,
  useLayoutState,
} from 'components/shared/Layout';

import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import CachedIcon from '@material-ui/icons/Cached';
import { countries } from 'data/countries';
import VerifiedUserIcon from '@material-ui/icons/VerifiedUser';

const useStyles = makeStyles((theme) => ({
  verifyCode: {
    width: 50,
    marginRight: theme.spacing(1),
    padding: 0,
    marginBottom: theme.spacing(1),
  },
  verify: {
    display: 'flex',
    lineHeight: theme.typography.pxToRem(25),
    '& svg': {
      color: theme.palette.success.main,
      marginRight: theme.spacing(0.5),
    },
  },
}));

const VerifyPhone = (props) => {
  const {
    i18n,
    data,
    setData,
    phoneDisplayValue = null,
    verifyPhoneNumberBtnClick = () => {},
  } = props;
  const classes = useStyles();
  const theme = useTheme();

  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const { layoutState } = useLayoutState();
  const [, setLoading] = useLayoutLoadingSpinner();

  const [snackbar, setSnackbar] = useLayoutSnackbar();
  const { handleError } = useHandleResponse();
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState([]);

  const handleVerifyPhone = (e) => {
    code[+e.target.name] = e.target.value;
    setCode([...code]);
    if (+e.target.name == 6) return false;
    e.target.value.length == 1 && document.getElementById(`input-${+e.target.name + 1}`).focus();
  };

  const handleClose = () => {
    setOpen(false);
    setCode([]);
  };
  const sendSMS = (phoneNumberInfo = null) => {
    setLoading(true);
    axios
      .post(API.users.sendSMS, {
        authenticity_token: layoutState.authenticity_token,
        phone: phoneNumberInfo.number.replace(phoneNumberInfo.phoneCode, '') || data.phone,
        phone_code: phoneNumberInfo.phoneCode || data.phone_code,
        country_code: phoneNumberInfo.countryCode || data.country_code,
      })
      .then(() => {
        setSnackbar({
          ...snackbar,
          open: true,
          severity: 'success',
          message: i18n('fe_es_identity_verification:sms_sent'),
        });
        setOpen(true);
      })
      .catch((error) => {
        switch (error.response.status) {
          case 406:
            if (error.response.data.error_code === 'ER0001') {
              setSnackbar({
                ...snackbar,
                open: true,
                severity: 'error',
                message: error.response.data.error_message,
              });
            } else {
              handleError(error);
            }
            break;
          default:
            handleError(error);
        }
      })
      .then(() => {
        setLoading(false);
      });
  };
  const verifyPhone = (token) => {
    setLoading(true);
    axios
      .post(API.users.verifyPhone, {
        authenticity_token: layoutState.authenticity_token,
        token,
      })
      .then((res) => {
        const currentPhoneCode = countries.filter(
          (item) => item.code === res.data.country_code.toUpperCase()
        )[0].phone;
        setData({
          ...res.data,
          time_zone: data.time_zone || null,
          time_zones: data.time_zones || null,
          phone_code: currentPhoneCode,
          phone: res.data.phone && res.data.phone.toString().replace(`+${currentPhoneCode}`, ''),
        });
        setSnackbar({
          ...snackbar,
          open: true,
          severity: 'success',
          message: i18n('fe_es_identity_verification:phone_verified'),
        });
        setOpen(false);
      })
      .catch((error) => {
        try {
          switch (error.response.status) {
            case 403:
              if (error.response.data.error_code == 'ER0001') {
                setSnackbar({
                  ...snackbar,
                  open: true,
                  severity: 'error',
                  message: i18n('fe_es_identity_verification:incorrect_code'),
                });
              }
              break;

            default:
              handleError(error);
          }
        } catch (e) {
          handleError(error);
        }
      })
      .then(() => {
        setLoading(false);
      });
  };

  const resendSMS = () => {
    setLoading(true);
    axios
      .post(API.users.resendSMS, {
        authenticity_token: layoutState.authenticity_token,
      })
      .then(() => {
        setSnackbar({
          ...snackbar,
          open: true,
          severity: 'success',
          message: i18n('fe_es_identity_verification:sms_sent'),
        });
      })
      .catch((error) => {
        handleError(error);
      })
      .then(() => {
        setLoading(false);
      });
  };
  const handleVerifyFieldKeyDown = (e) => {
    if (e.code === 'Backspace' && !e.target.value) {
      // queue the focus in the macrotask queue to avoid focusing before the change handler takes place
      setTimeout(() => {
        document.getElementById(`input-${e.target.name - 1}`)?.focus();
      }, 0);
    }
  };
  useEffect(() => {
    const token = code.join().replaceAll(',', '');
    token.length === 7 && verifyPhone(token);
  }, [code]);
  return (
    <>
      {!data.phone_verified ? (
        <>
          <Button
            disabled={!phoneDisplayValue || data.email === ''}
            onClick={() => {
              const phoneNumberInfo = verifyPhoneNumberBtnClick();
              if (phoneNumberInfo.status) {
                sendSMS(phoneNumberInfo);
              }
            }}
            color="default"
            variant="outlined"
          >
            {i18n('fe_es_identity_verification:verify_number')}
          </Button>
        </>
      ) : (
        <Typography className={classes.verify} variant="body2" color="textSecondary">
          <VerifiedUserIcon color="info" />
          <span>{i18n('fe_es_identity_verification:phone_verified_text')}</span>
        </Typography>
      )}

      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="form-dialog-title"
        maxWidth="md"
        fullScreen={fullScreen}
      >
        <DialogTitle id="form-dialog-title">
          {i18n('fe_es_identity_verification:confirm_number')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {i18n('fe_es_identity_verification:enter_code')}
            {' +'}
            {data.phone_code}
            {data.phone}
          </DialogContentText>
          <Box display="flex" flexWrap="wrap">
            {Array.from(Array(7).keys()).map((num) => (
              <TextField
                key={num}
                value={code[num]}
                name={num}
                autoFocus={num === 0}
                onKeyDown={handleVerifyFieldKeyDown}
                maxLength={1}
                variant="outlined"
                type="tel"
                id={`input-${num}`}
                className={classes.verifyCode}
                onChange={handleVerifyPhone}
              />
            ))}
          </Box>
          <Box>
            {i18n('fe_es_identity_verification:did_not_get_msg')}{' '}
            <Button onClick={resendSMS} color="default" endIcon={<CachedIcon />}>
              {i18n('fe_es_identity_verification:send_again')}
            </Button>
          </Box>
          <Box>
            <Button style={{ textDecoration: 'underline' }} onClick={handleClose}>
              {i18n('fe_es_identity_verification:change_number')}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default VerifyPhone;
